import { useState, useEffect } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { EmptyState } from '../../../ui';
import { LoadingSpinner } from '../../../components/common/LoadingSpinner';
import { useCompanies, useCashFlowSummary } from '../../../hooks/useFinancialOps';

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
}

function getDefaultDates() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - 5, 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  return {
    start: start.toISOString().slice(0, 10),
    end: end.toISOString().slice(0, 10),
  };
}

export function FluxoCaixaPage() {
  const { data: companiesData } = useCompanies({ active: true });
  const companies = companiesData?.data ?? [];

  const defaults = getDefaultDates();
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [startDate, setStartDate] = useState(defaults.start);
  const [endDate, setEndDate] = useState(defaults.end);

  useEffect(() => {
    if (!selectedCompanyId && companies.length === 1) {
      setSelectedCompanyId(companies[0].id);
    }
  }, [companies, selectedCompanyId]);

  const { data, isLoading } = useCashFlowSummary(selectedCompanyId, startDate, endDate);
  const summary = data?.data;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Empresa
          </label>
          <select
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink min-w-[200px]"
            value={selectedCompanyId}
            onChange={(e) => setSelectedCompanyId(e.target.value)}
          >
            <option value="">Selecione...</option>
            {companies.map((c: any) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            De
          </label>
          <input
            type="date"
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-ink-secondary uppercase tracking-wider mb-1.5">
            Até
          </label>
          <input
            type="date"
            className="h-9 border border-border rounded-md px-3 text-sm bg-surface text-ink"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
      </div>

      {!selectedCompanyId ? (
        <EmptyState
          title="Selecione uma empresa"
          body="Escolha uma empresa para ver o fluxo de caixa."
        />
      ) : isLoading ? (
        <div className="flex justify-center py-12">
          <LoadingSpinner size="lg" />
        </div>
      ) : !summary ? (
        <EmptyState
          title="Sem dados"
          body="Nenhuma transação encontrada para o período selecionado."
        />
      ) : summary.totalIncome === 0 &&
        summary.totalExpense === 0 &&
        summary.pendingIncome === 0 &&
        summary.pendingExpense === 0 ? (
        <EmptyState
          title="Sem transações no período"
          body="Nenhuma transação (paga ou pendente) encontrada para o intervalo selecionado. Verifique as datas ou cadastre/confirme transações."
        />
      ) : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="border border-border rounded-lg p-4 bg-surface">
              <p className="text-xs text-ink-secondary uppercase tracking-wider font-medium">
                Receitas Confirmadas
              </p>
              <p className="text-2xl font-semibold text-accent mt-1">
                {formatCurrency(summary.totalIncome)}
              </p>
            </div>
            <div className="border border-border rounded-lg p-4 bg-surface">
              <p className="text-xs text-ink-secondary uppercase tracking-wider font-medium">
                Despesas Confirmadas
              </p>
              <p className="text-2xl font-semibold text-danger mt-1">
                {formatCurrency(summary.totalExpense)}
              </p>
            </div>
            <div className="border border-border rounded-lg p-4 bg-surface">
              <p className="text-xs text-ink-secondary uppercase tracking-wider font-medium">
                Saldo
              </p>
              <p
                className={`text-2xl font-semibold mt-1 ${summary.balance >= 0 ? 'text-accent' : 'text-danger'}`}
              >
                {formatCurrency(summary.balance)}
              </p>
            </div>
            <div className="border border-amber-500/40 rounded-lg p-4 bg-surface">
              <p className="text-xs text-amber-500 uppercase tracking-wider font-medium">
                Pendente a Confirmar
              </p>
              <p className="text-2xl font-semibold text-amber-500 mt-1">
                {formatCurrency(summary.pendingBalance)}
              </p>
              <p className="text-xs text-ink-tertiary mt-1">
                {formatCurrency(summary.pendingIncome)} receitas ·{' '}
                {formatCurrency(summary.pendingExpense)} despesas
              </p>
            </div>
          </div>

          {summary.byPeriod.length > 0 && (
            <div className="border border-border rounded-lg p-4 bg-surface">
              <h3 className="text-sm font-semibold text-ink mb-4">Receitas vs Despesas por Mês</h3>
              <ResponsiveContainer width="100%" height={280}>
                <BarChart
                  data={summary.byPeriod}
                  margin={{ top: 0, right: 0, left: 20, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis
                    dataKey="period"
                    tick={{ fontSize: 12, fill: 'var(--ink-secondary)' }}
                  />
                  <YAxis
                    tick={{ fontSize: 12, fill: 'var(--ink-secondary)' }}
                    tickFormatter={(v) => `R$ ${(v / 1000).toFixed(0)}k`}
                  />
                  <Tooltip
                    formatter={(value) => formatCurrency(Number(value))}
                    contentStyle={{
                      backgroundColor: 'var(--bg-surface)',
                      border: '1px solid var(--border)',
                    }}
                  />
                  <Legend />
                  <Bar
                    dataKey="income"
                    name="Receitas"
                    fill="var(--accent)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="expense"
                    name="Despesas"
                    fill="var(--danger)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </>
      )}
    </div>
  );
}
